// What a web Session Replay may keep: the page's shape and the user's
// interactions, never their words (#510). Bosko's screens are full of private
// writing (Journal entries and moods, chats, prayer intentions, account
// details), and a replay reaches Sentry through more than the DOM:
//
//   - DOM text and input values: masked by the SDK options in sentry.js.
//   - DOM attributes: rrweb masks only title/placeholder/aria-label. href, src
//     and style skip masking entirely, and class, data-* and ARIA state are
//     copied verbatim, hence REPLAY_BLOCK here and `data-sentry-block` on
//     components whose classes or styles encode private state (moods, prayer
//     categories, Bible highlights). URL attributes are scrubbed on the
//     server (below).
//   - Custom events (breadcrumbs, performance spans): their URLs keep the query
//     string, and the replay's own click breadcrumbs are built from the live
//     DOM. scrubRecordingEvent rewrites them.
//   - The replay_event: its URL list and request. beforeSend never runs for
//     replays; scrubReplayEvent runs as an event processor.
//
// Two parts of a recording no browser hook reaches, because rrweb writes them
// before the SDK sees an event: the location.href of each full snapshot, and
// element URL attributes (a profile link names a friend). The tunnel rewrites
// both before forwarding (SentryReplayScrubber, #518), using the same rules as
// scrubUrl. Replay still doesn't start on a bearer-link landing, and free text
// stays out of the address bar where it is pure client state. Pointer
// coordinates are recorded even over blocked nodes (no SDK switch in 10.52.0).
import { redactPrivatePaths } from '@/lib/private-paths';

// Active Storage blob and representation links never expire and need no
// session: a journal photo's link is the photo.
const ACTIVE_STORAGE_PATH = /(\/rails\/active_storage\/)[^?#\s"]*/g;

// Keep where a request went, never what rode along in its query: our own
// routes put typed search text, email addresses and payment ids there. Bearer
// tokens and friends' handles in the path are redacted. Mirrors
// SentryPrivatePaths.scrub_url on the server.
export function scrubUrl(value) {
  if (typeof value !== 'string') return value;
  const [path] = value.split(/[?#]/, 1);
  return redactPrivatePaths(path).replace(ACTIVE_STORAGE_PATH, '$1[redacted]');
}

// rrweb masks character by character, which keeps the text's length and word
// count, and a short label from a fixed vocabulary (a mood, a prayer category)
// is readable from either in some locales. Every non-blank text becomes the
// same token.
export function maskReplayText(text) {
  if (typeof text !== 'string') return '';
  return text.trim() ? '***' : text;
}

// href cannot be masked, so links that can carry someone's words, an address
// or a token are blocked outright; a blocked node keeps only its size and
// class. (The tunnel scrubs the hrefs left, too.)
export const REPLAY_BLOCK = Object.freeze([
  'a[href^="http"]',
  'a[href^="mailto:"]',
  'a[href^="tel:"]',
  'a[href*="?"]',
  // Links typed into an editor (the bio editor) are the author's words, in
  // any form: relative, protocol-relative or upper-case.
  '[contenteditable] a',
  'link[rel="canonical"]',
  'link[rel="alternate"]',
]);

// maskAllInputs skips hidden inputs; one carries the CSRF token.
export const REPLAY_MASK = Object.freeze(['input[type="hidden"]']);

// Attributes rrweb copies verbatim unless listed (the SDK default is the first
// three). Today the others carry only static copy; listing them keeps "all
// text is hidden" true if one ever carries someone's words.
export const REPLAY_MASK_ATTRIBUTES = Object.freeze([
  'title',
  'placeholder',
  'aria-label',
  'aria-description',
  'aria-valuetext',
  'aria-roledescription',
  'alt',
]);

// The tunnel forwards only replays carrying this tag, so a tab still running a
// bundle from before these scrubbers can't send one (SentryTunnelMiddleware).
export const REPLAY_PRIVACY_TAG = Object.freeze({ replay_privacy: 'masked' });

const URL_FIELDS = ['url', 'from', 'to', 'route', 'previous'];

// An image's address identifies the image: a sign-in provider's avatar URL is
// the person's photo, and nothing in its path looks private. Keep the host.
export const IMAGE_SPANS = new Set(['resource.img', 'resource.image']);

export function imageHost(value) {
  try {
    return new URL(value).origin;
  } catch {
    return scrubUrl(value);
  }
}

// The SDK names an element as tag#id.class… followed by its aria-label, type,
// name, title and alt as [key="value"], ancestors joined by " > "
// (htmlTreeAsString, @sentry/core 10.52.0). Click and key breadcrumbs, INP
// span names, lcp.element and cls.source.N are all built that way, and an
// avatar's alt or a button's aria-label names another person (#520). Tags,
// ids and classes are ours and stay; of the values, only a control's type.
// Values aren't escaped, so one holding `"]` ends the match early and leaves
// a quote behind: then nothing from the first attribute on is kept.
const SELECTOR_ATTRIBUTE = /\[([\w:-]+)="([\s\S]*?)"\]/g;
const SELECTOR_ATTRIBUTE_START = /\[[\w:-]+="/;
const KEPT_TYPE = /^[a-z-]+$/;
// The SDK caps the ancestors, not the element itself. A longer description is
// cut at its first attribute without running the pattern.
const MAX_SELECTOR_LENGTH = 2048;

export function scrubSelector(selector) {
  if (typeof selector !== 'string') return selector;
  const start = selector.search(SELECTOR_ATTRIBUTE_START);
  if (start === -1) return selector;
  if (
    selector.length > MAX_SELECTOR_LENGTH ||
    selector.replace(SELECTOR_ATTRIBUTE, '[$1]').includes('"')
  ) {
    return selector.slice(0, start);
  }
  return selector.replace(SELECTOR_ATTRIBUTE, (attribute, key, value) =>
    key === 'type' && KEPT_TYPE.test(value) ? attribute : `[${key}]`,
  );
}

// Spans named after an element rather than a request: INP and other
// interactions, and standalone LCP and CLS spans.
export const SELECTOR_SPAN_OPS = /^ui\.(?:interaction|webvital)\./;

// Attributes describing an element: a page load's LCP element and its
// layout-shift sources (prefixed browser.web_vital. when spans stream).
const SELECTOR_KEY = /(?:^|\.)(?:lcp\.element|cls\.source\.\d+)$/;

// A long animation frame names the element whose listener ran: IMG#id.onload,
// or without an id IMG[src=<url>].onload. An avatar's address is the person's
// photo, so keep its host, as for any image span.
const INVOKER_SRC = /\[src=(.*)\]/s;

export function scrubSpanData(data) {
  if (!data) return;
  for (const key of Object.keys(data)) {
    if (SELECTOR_KEY.test(key)) data[key] = scrubSelector(data[key]);
  }
  const invoker = data['browser.script.invoker'];
  if (typeof invoker === 'string') {
    data['browser.script.invoker'] = invoker.replace(
      INVOKER_SRC,
      (_match, src) => `[src=${imageHost(src)}]`,
    );
  }
}

function scrubUrlFields(data) {
  if (!data) return;
  for (const key of URL_FIELDS) {
    if (typeof data[key] === 'string') data[key] = scrubUrl(data[key]);
  }
}

// beforeAddRecordingEvent: runs on custom recording events only. A throw drops
// the event, so a malformed one fails closed.
export function scrubRecordingEvent(event) {
  const { tag, payload } = event?.data ?? {};
  if (!payload) return event;

  if (tag === 'performanceSpan') {
    payload.description = IMAGE_SPANS.has(payload.op)
      ? imageHost(payload.description)
      : scrubUrl(payload.description);
    scrubUrlFields(payload.data);
  } else if (tag === 'breadcrumb') {
    scrubUrlFields(payload.data);
    const category = String(payload.category ?? '');
    if (category.startsWith('ui.')) {
      // Click and key breadcrumbs describe the live element: its raw alt,
      // aria-label and title, and every class. Error reports keep the classes
      // and drop only the values (scrubSelector); a replay records the layout
      // around the click anyway, so it keeps the tag name alone.
      const node = payload.data?.node;
      payload.message = String(node?.tagName ?? 'element').toLowerCase();
      if (node) node.attributes = {};
    } else if (category === 'console' && payload.data) {
      delete payload.data.arguments;
    }
  }
  return event;
}

export function scrubReplayEvent(event) {
  if (event?.type !== 'replay_event') return event;
  // The scope's own breadcrumbs (raw click targets, console arguments) are
  // merged into every event. The SDK drops them from replays only while
  // replay is enabled, which a stopping replay's last segment isn't; the
  // recording already carries scrubbed copies.
  delete event.breadcrumbs;
  if (Array.isArray(event.urls)) event.urls = event.urls.map(scrubUrl);
  // The scope's transaction name, the current pathname, rides along too.
  if (event.transaction) event.transaction = scrubUrl(event.transaction);
  if (event.request) {
    if (event.request.url) event.request.url = scrubUrl(event.request.url);
    const headers = event.request.headers;
    if (headers?.Referer) headers.Referer = scrubUrl(headers.Referer);
  }
  event.tags = { ...event.tags, ...REPLAY_PRIVACY_TAG };
  return event;
}
